import { useState, useCallback, useEffect } from 'react';
import { Button, Typography, Input, Modal, Space, Card, Row, Col, notification } from 'antd';
import { SearchOutlined, CloseOutlined, PlusOutlined, TeamOutlined } from '@ant-design/icons';
import AddNewLeads from './AddLeads/AddNewLeads';
import LeadsDashboard from './LeadDashboard';
import { debounce } from 'lodash';
import { getLeads } from '@/api/novi/leads';
import { useQuery } from '@tanstack/react-query';

const { Title, Text } = Typography;

const PAGE_SIZE = 100;

interface LeadResponse {
	leads: any[];
	pagination: {
		nextKey: string | null;
		hasMore: boolean;
	};
}

const LeadsInfo: React.FC<{ campaignId: string; isScheduleActive?: boolean }> = ({
	campaignId,
	isScheduleActive,
}) => {
	const [searchText, setSearchText] = useState('');
	const [addLeadModal, setAddLeadModal] = useState(false);
	const [leadsData, setLeadsData] = useState<Record<string, any>>({
		NEW: {
			leads: [],
			hasMore: true,
			nextKey: null,
			loading: false,
			tooltip:
				'Lead which has been added recently and has not been utilized by calling or texting schedule',
		},
		CONTACTED: {
			leads: [],
			hasMore: true,
			nextKey: null,
			loading: false,
			tooltip: 'Leads that have been successfully reached and communicated with',
		},
		PROCESSING: {
			leads: [],
			hasMore: true,
			nextKey: null,
			loading: false,
			tooltip: 'Leads currently being processed in active schedule or pending follow-up actions',
		},
		FAILED: {
			leads: [],
			hasMore: true,
			nextKey: null,
			loading: false,
			tooltip:
				"Leads that couldn't be reached after maximum retry attempts or encountered communication errors",
		},
	});

	const fetchLeadsByStatus = useCallback(
		async (status: string, nextKey?: string | null) => {
			setLeadsData((prev) => ({
				...prev,
				[status]: { ...prev[status], loading: true },
			}));

			try {
				// If we're searching, don't include status parameter
				const queryParams: any = {
					limit: PAGE_SIZE,
				};

				// Only add status if not searching
				if (!searchText) {
					queryParams.status = status;
				}

				// Add phone number for searching
				if (searchText) {
					queryParams.phoneNumber = searchText;
				}

				// Add nextKey if it exists
				if (nextKey) {
					queryParams.nextKey = nextKey;
				}

				const response: LeadResponse = await getLeads(campaignId, queryParams);

				if (searchText) {
					// Filter the results by status for search results
					const filteredLeads = response.leads.filter((lead) => lead.status === status);

					setLeadsData((prev) => ({
						...prev,
						[status]: {
							...prev[status],
							leads: filteredLeads,
							hasMore: response.pagination.hasMore,
							nextKey: response.pagination.nextKey || null,
							loading: false,
						},
					}));
				} else {
					// Normal case - no search
					setLeadsData((prev) => ({
						...prev,
						[status]: {
							...prev[status],
							leads: nextKey ? [...prev[status].leads, ...response.leads] : [...response.leads],
							hasMore: response.pagination.hasMore,
							nextKey: response.pagination.nextKey || null,
							loading: false,
						},
					}));
				}
			} catch (error) {
				notification.error({
					message: 'Error',
					description: 'Failed to fetch leads',
				});
				setLeadsData((prev) => ({
					...prev,
					[status]: { ...prev[status], loading: false },
				}));
			}
		},
		[campaignId, searchText],
	);

	// Effect to refetch leads when search text changes
	useEffect(() => {
		if (campaignId) {
			// Reset all leads data when search changes
			Object.keys(leadsData).forEach((status) => {
				setLeadsData((prev) => ({
					...prev,
					[status]: {
						...prev[status],
						leads: [],
						nextKey: null,
						hasMore: true,
					},
				}));
			});

			// Fetch all statuses with the new search
			Object.keys(leadsData).forEach((status) => fetchLeadsByStatus(status));
		}
	}, [searchText, campaignId]);

	useEffect(() => {
		if (campaignId) {
			Object.keys(leadsData).forEach((status) => fetchLeadsByStatus(status));
		}
	}, [campaignId]);

	useQuery({
		queryKey: ['leads', campaignId, searchText],
		queryFn: async () => {
			await Promise.all(Object.keys(leadsData).map((status) => fetchLeadsByStatus(status)));
			return leadsData;
		},
		enabled: !!campaignId,
		refetchOnMount: false,
	});

	const handleSearch = debounce((value: string) => {
		setSearchText(value.trim());
	}, 300);

	const closeAddLeadModal = () => {
		setAddLeadModal(false);
	};

	return (
		<>
			<Space direction="vertical" style={{ width: '100%', gap: '12px' }}>
				<Card
					bordered={false}
					className="mb-2 shadow-md rounded-lg bg-white"
					bodyStyle={{ padding: '18px 24px' }}
				>
					<Row justify="space-between" align="middle" gutter={[16, 12]}>
						{/* Leads Title with Centered Icon */}
						<Col xs={24} sm={8} md={6} lg={6}>
							<Space align="center">
								<TeamOutlined className="text-blue-500 text-2xl" />
								<Title level={4} className="mt-2 text-lg text-gray-800">
									Leads
								</Title>
							</Space>
						</Col>

						{/* Search Input with Icon */}
						<Col xs={24} sm={8} md={10} lg={12}>
							<Input
								placeholder="Search leads by phone number..."
								allowClear
								onChange={(e) => handleSearch(e.target.value)}
								prefix={<SearchOutlined className="text-gray-500 text-md" />}
								className="w-full rounded-md border-gray-300 shadow-sm px-3 py-2 text-md"
							/>
						</Col>

						{/* Add Lead Button with Icon */}
						<Col xs={24} sm={8} md={8} lg={6} className="text-right">
							<Button
								type="primary"
								icon={<PlusOutlined />}
								size="large"
								className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-md shadow"
								onClick={() => setAddLeadModal(true)}
							>
								Add Lead
							</Button>
						</Col>
					</Row>
				</Card>

				<LeadsDashboard
					isScheduleActive={isScheduleActive as boolean}
					campaignID={campaignId}
					searchQuery={searchText}
					leads={leadsData}
					refetchLeads={fetchLeadsByStatus}
				/>
			</Space>
			<Modal
				open={addLeadModal}
				onCancel={closeAddLeadModal}
				footer={null}
				width={800}
				closeIcon={<CloseOutlined />}
				title={
					<div style={{ padding: '12px 0' }}>
						<Title level={4} style={{ margin: 0 }}>
							Add New Leads
						</Title>
						<Text type="secondary">Choose how you'd like to import your leads</Text>
					</div>
				}
			>
				<AddNewLeads closeModal={closeAddLeadModal} refetchLeads={fetchLeadsByStatus} />
			</Modal>
		</>
	);
};

export default LeadsInfo;
